import type { ReactNode } from 'react'
import { Button, ProgressBar } from '@systemeio/components'
import { ArrowLeftIcon, GraduationHat01Icon, File05Icon } from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useFormatDateTime } from 'shared/hooks/use-format-date-time'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'

type SchoolCardProgress =
  | {
      completedLecturesCount: number
      totalLecturesCount: number
      completedCoursesCount?: never
      totalCoursesCount?: never
    }
  | {
      completedCoursesCount: number
      totalCoursesCount: number
      completedLecturesCount?: never
      totalLecturesCount?: never
    }
  | {
      completedCoursesCount?: never
      totalCoursesCount?: never
      completedLecturesCount?: never
      totalLecturesCount: number
    }

export type SchoolCardProps = {
  onClick?: () => void
  href: string | null
  target: string | null
  courseCover: string | null
  variant: 'white' | 'dark'
  title: string
  instructorName: string | null
  description: string | null
  accessFrom: string | null
  progress?: number
} & SchoolCardProgress

export const SchoolCard = ({
  onClick,
  href,
  target,
  courseCover,
  variant,
  title,
  instructorName,
  description,
  completedCoursesCount,
  completedLecturesCount,
  totalCoursesCount,
  totalLecturesCount,
  accessFrom,
  progress,
}: SchoolCardProps): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const accessFromFormatted = useFormatDateTime(accessFrom)
  const isDark = variant === 'dark'
  const hasProgressCount =
    completedCoursesCount !== undefined || completedLecturesCount !== undefined
  const hasUpcomingAccess = !!accessFrom && isAuthorizedUser(user)
  const progressLabel = progress === undefined ? null : `${progress}%`
  const buttonLabel = hasProgressCount
    ? progress
      ? t('school.card.button.resume_course')
      : t('school.card.button.open_course')
    : t('school.card.button.get_access_to_course')

  const cardClassName = cn(
    'relative flex min-w-0 flex-col rounded-2xl',
    isDark ? 'bg-text-primary' : 'bg-bg-primary',
    onClick && 'cursor-pointer',
  )

  const cardBody = (
    <>
      <div className="relative aspect-video">
        <div
          className={cn(
            'absolute inset-0 size-full overflow-hidden rounded-t-2xl',
            isDark &&
              'before:absolute before:inset-0 before:rounded-t-2xl before:bg-linear-to-b before:from-system-secondary-900/20 before:to-system-secondary-900',
          )}
        >
          {onClick && (
            <button
              type="button"
              className="absolute top-4 right-4 z-10 flex cursor-pointer gap-1 rounded-full bg-bg-primary px-4 py-1 text-sm leading-[23px] font-semibold text-text-primary"
              onClick={event => {
                event.stopPropagation()
                onClick()
              }}
            >
              {t('school.course_bundle_badge')}
            </button>
          )}
          {courseCover ? (
            <img className="block size-full object-cover object-center" src={courseCover} alt="" />
          ) : (
            <div className="flex size-full items-center justify-center rounded-t-2xl bg-bg-accent-active">
              <GraduationHat01Icon className="size-1/2 text-text-on" />
            </div>
          )}
        </div>
      </div>
      <div className="px-6 pt-6">
        <h5
          className={cn(
            'text-xl font-bold wrap-anywhere',
            isDark ? 'text-text-on' : 'text-text-primary',
          )}
          data-test-id="course-header"
        >
          {title}
        </h5>
        <p
          data-test-id="author-course"
          className={cn(
            'mt-2 text-base font-normal',
            isDark ? 'text-text-on' : 'text-text-secondary',
            !instructorName && 'opacity-0',
          )}
        >
          {instructorName || '.'}
        </p>
        {description && (
          <p
            className={cn(
              'mt-2 max-h-28 overflow-y-auto text-base wrap-break-word whitespace-pre-line [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:transition-opacity',
              isDark
                ? '[&::-webkit-scrollbar-thumb]:bg-bg-accent-active/0 [&:hover::-webkit-scrollbar-thumb]:bg-bg-accent-active'
                : '[&::-webkit-scrollbar-thumb]:bg-text-tertiary/0 [&:hover::-webkit-scrollbar-thumb]:bg-text-tertiary',
              isDark ? 'text-text-secondary' : 'text-text-primary/70',
            )}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>
      <div className="mt-auto px-6 pb-6">
        {hasUpcomingAccess ? (
          <p className="mt-2 text-sm text-text-primary/70">
            {t('school.card.waiting_for_access', {
              date: accessFromFormatted?.formatted,
            })}
          </p>
        ) : (
          progress !== undefined && (
            <ProgressBar
              value={progress}
              valueLabel={progressLabel}
              rootProps={{
                'data-test-id': 'course-progress-bar',
                className: 'mt-6 w-full',
              }}
            />
          )
        )}
        <div className="mt-8 flex justify-between">
          <span
            data-test-id="amount-lessons"
            className="flex items-center gap-1 text-base text-text-secondary"
          >
            <File05Icon
              className={cn('size-5', isDark ? 'text-text-on' : 'text-text-primary')}
              fill="currentColor"
            />
            {t(
              completedCoursesCount !== undefined
                ? 'school.card.courses_counter'
                : 'school.card.lessons_counter',
              {
                progress: `${
                  completedCoursesCount !== undefined
                    ? `${completedCoursesCount}/`
                    : completedLecturesCount !== undefined
                      ? `${completedLecturesCount}/`
                      : ''
                }${totalCoursesCount !== undefined ? totalCoursesCount : totalLecturesCount}`,
              },
            )}
          </span>
          {!accessFrom && href && (
            <Button
              variant="primary"
              render={
                <LinkWithoutPrefetch href={href} target={target ?? undefined} onClick={onClick} />
              }
            >
              {buttonLabel}
              <ArrowLeftIcon className="size-5 rotate-180" />
            </Button>
          )}
        </div>
      </div>
    </>
  )

  if (href) {
    return (
      <LinkWithoutPrefetch
        href={href}
        target={target ?? undefined}
        className={cardClassName}
        onClick={onClick}
      >
        {cardBody}
      </LinkWithoutPrefetch>
    )
  }

  return (
    <div className={cardClassName} onClick={onClick}>
      {cardBody}
    </div>
  )
}
